<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useDialog } from 'primevue/usedialog';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import MaxRoomConfigModal from '@/components/admin/MaxRoomConfiguration/MaxRoomConfigModal.vue';
import MinimumGroupSizeConfig from '@/components/admin/MaxRoomConfiguration/MinimumGroupSizeConfig.vue';

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const adminSetupStore = useAdminSetupStore();
const hotelManagementStore = useHotelManagementStore();
const packageManagementStore = usePackageManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const maxRoomConfigs = ref([]);
const destinations = ref<any>([]);
const hotels = ref([]);
const isLoading = ref(false);
const isLoadingDestination = ref(false);
const isLoadingHotel = ref(false);

const isHide = computed(() => {
  return authorizations?.configurationPage?.configurable_items_edit;
});

const displayData = (data: any) => {
  return data ? data.join(' | ') : '';
};

const displayDataDestinations = (destinationCodes: any) => {
  const destinationSelected = destinations.value
    .filter((destination: any) => {
      return destinationCodes.includes(destination.code);
    })
    .map((item: any) => item.airportCode);

  return destinationSelected.length ? destinationSelected.join(' | ') : '';
};

const displayDataHotels = (hotelCodes: any) => {
  const hotelSelected = hotels.value
    .filter((hotel: any) => {
      return hotelCodes.includes(hotel.hCode);
    })
    .map((hotel: any) => hotel.hCode);

  return hotelSelected.length ? hotelSelected.join(' | ') : '';
};

const displayDataRooms = (roomCodes: any) => {
  return roomCodes.length ? roomCodes.join(' | ') : '';
};

const getListDestination = () => {
  isLoadingDestination.value = true;
  packageManagementStore
    .getListDestination()
    .then((res: any) => {
      destinations.value = res;
    })
    .finally(() => {
      isLoadingDestination.value = false;
    });
};

const getListHotel = () => {
  isLoadingHotel.value = true;
  hotelManagementStore
    .getListHotel()
    .then((res: any) => {
      hotels.value = res;
    })
    .finally(() => {
      isLoadingHotel.value = false;
    });
};

const getMaxRoomsConfiguration = () => {
  isLoading.value = true;
  adminSetupStore
    .getMaxRoomConfig()
    .then((res: any) => {
      if (res) {
        maxRoomConfigs.value = res;
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const openModalEditMaxRoomConfig = () => {
  const dialogRef = dialog.open(MaxRoomConfigModal, {
    props: {
      header: t('quote_config.add_max_room_restriction'),
      modal: true,
      maximizable: true,
      style: {
        width: '90vw'
      },
      breakpoints: {
        '960px': '60vw',
        '640px': '90vw'
      }
    },
    data: {
      destinations: destinations.value,
      hotels: hotels.value,
      maxRoomConfigs: maxRoomConfigs.value
    },
    emits: {
      onClose: () => {
        dialogRef.close();
        getMaxRoomsConfiguration();
      }
    }
  });
};

onMounted(() => {
  getListDestination();
  getListHotel();
  getMaxRoomsConfiguration();
});
</script>
<template>
  <div class="min-max-config">
    <LoadingSpinner v-if="isLoadingDestination || isLoadingHotel || isLoading" />
    <MinimumGroupSizeConfig></MinimumGroupSizeConfig>
    <div class="card max-room">
      <div class="flex flex-wrap justify-content-between">
        <div class="open-jaw">
          <h5 class="title">{{ $t('quote_config.max_room_restrictions') }}</h5>
        </div>
      </div>
      <div class="flex" v-if="maxRoomConfigs.length">
        <div class="col-2">{{ $t('quote_config.brochures') }}</div>
        <div class="col-2">{{ $t('quote_config.destinations') }}</div>
        <div class="col-2">{{ $t('quote_config.hotel_code') }}</div>
        <div class="col-2">{{ $t('book_form.room_category') }}</div>
        <div class="col-2">{{ $t('quote_config.max_rooms') }}</div>
      </div>
      <div class="flex mb-2" v-for="(maxRoomConfig, index) in maxRoomConfigs" :key="index">
        <div class="col-2">
          <InputText readonly :modelValue="displayData(maxRoomConfig?.brochureCodes)"></InputText>
        </div>
        <div class="col-2">
          <InputText readonly :modelValue="displayDataDestinations(maxRoomConfig?.destinationCodes)"></InputText>
        </div>
        <div class="col-2">
          <InputText readonly :modelValue="displayDataHotels(maxRoomConfig?.hotelCodes)"></InputText>
        </div>
        <div class="col-2">
          <InputText readonly :modelValue="displayDataRooms(maxRoomConfig?.roomCodes)"></InputText>
        </div>
        <div class="col-2">
          <InputNumber readonly :modelValue="maxRoomConfig?.maxRooms"></InputNumber>
        </div>
      </div>
      <div class="flex mb-2">
        <div class="col-10 text-right">
          <Button v-if="isHide" id="edit-button-id" icon="pi pi-pencil" :label="$t('common.buttons.edit')" @click="openModalEditMaxRoomConfig()"></Button>
        </div>
      </div>
      <div class="flex">
        <div class="col-10 text-right">
          {{ '*' + $t('quote_config.note_max_room_config') }}
        </div>
      </div>
    </div>
  </div>
</template>
